import {
  forwardRef,
  MouseEvent,
  MouseEventHandler,
  CSSProperties,
  ReactNode,
  Ref,
} from "react";
import styled, { css } from "styled-components";
import ToolTip from "../tooltip/ToolTip";

/**
 * 汎用アイコンボタン。
 *
 * 旧実装は 2 種類のアイコンボタンが共存していた:
 *   - `IconButton`        … `<div>` ベース、24px 固定、children でアイコン名を受ける
 *   - `StatusIconButton`  … `<button>` ベース、可変サイズ、丸背景 hover
 *
 * これらを 1 つに集約する。
 *
 * 主な差分の吸収:
 *  - DOM タグは `<button>` で統一（フォーカス順 / a11y 改善）。ref も
 *    `HTMLButtonElement` を返す（Tippy は button でも問題なく動作）。
 *  - サイズは `size` prop で任意指定。既定 24（旧 IconButton と同じ）。
 *  - `variant`:
 *      - `"default"` （旧 IconButton 相当）: 透過、hover で青 + scale 1.1
 *      - `"circle"`  （旧 StatusIconButton 相当）: 丸い当たり判定、hover で青 + scale 1.15
 *  - `filled` で Material Symbols の FILL 軸を切り替え可能。
 *  - icon は **`icon` prop** で指定するのが推奨。後方互換で `children` も受ける。
 */
export interface IconButtonProps {
  /** Material Symbols のアイコン名。新規コードはこちらを使う。 */
  icon?: string;
  /** 旧 IconButton API。`icon` が未指定のときに利用される（後方互換）。 */
  children?: ReactNode;
  /** クリックハンドラ。 */
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  /** 無効化（半透明 + クリック不可）。 */
  disabled?: boolean;
  /** アイコンサイズ（px）。既定 24。 */
  size?: number;
  /** 視覚バリアント。`"circle"` は旧 StatusIconButton 相当の丸い当たり判定。 */
  variant?: "default" | "circle";
  /**
   * Material Symbols の FILL 軸。
   * - `true`  → `'FILL' 1`
   * - `false` → `'FILL' 0`
   * - `undefined`（既定）→ `font-variation-settings` を出力しない（プロジェクト全体の
   *   デフォルト設定 / フォント側デフォルトを尊重する）。旧 IconButton 互換挙動。
   */
  filled?: boolean;
  /**
   * 通常時のアイコンカラー（CSS color 値）。
   * 既定 `var(--soremo-light-gray)`。重要度低めの UI には `var(--soremo-border)` 等を渡す。
   */
  color?: string;
  /**
   * ホバー時のアイコンカラー。既定 `var(--soremo-blue)`。
   * 色変化を抑制したい場合は同じ色を指定する。
   */
  hoverColor?: string;
  /** ホバー時に表示するグローバル ToolTip の文言。 */
  title?: string;
  /** スクリーンリーダー向けラベル。 */
  ariaLabel?: string;
  /**
   * 指定時は `<a>` として描画し、本物のリンクにする（中クリック / リンクコピー対応）。
   * `disabled` との併用は想定しない（disabled のときは従来どおり button で描画する）。
   */
  href?: string;
  /** `href` とあわせて使用。`"_blank"` のときは rel="noopener noreferrer" を付ける。 */
  target?: string;
  className?: string;
  style?: CSSProperties;
}

const StyledButton = styled.button<{
  $size: number;
  $variant: "default" | "circle";
  $filled?: boolean;
  $color: string;
  $hoverColor: string;
  disabled?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};
  flex-shrink: 0;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  transition: transform 0.15s ease;
  /* href 指定時は <a> として描画される（as="a"）。UA の下線を消す */
  text-decoration: none;

  ${({ $variant, $size }) =>
    $variant === "circle"
      ? css`
          width: ${$size}px;
          height: ${$size}px;
          border-radius: 50%;
        `
      : css`
          /* 旧 IconButton と同じく、ヒット領域はアイコンと同等。
           * スケールアニメ用の余白を確保。 */
          line-height: 1;
        `}

  & > .material-symbols-rounded {
    font-size: ${({ $size }) => $size}px;
    color: ${({ $color }) => $color};
    ${({ $filled }) =>
      $filled === undefined
        ? ""
        : `font-variation-settings: ${$filled ? "'FILL' 1" : "'FILL' 0"};`}
    transition: color 0.2s ease, scale 0.2s ease;
  }

  @media (hover: hover) {
    &:hover:not(:disabled) {
      & > .material-symbols-rounded {
        color: ${({ $hoverColor }) => $hoverColor};
        scale: ${({ $variant }) => ($variant === "circle" ? "1.15" : "1.1")};
      }
    }
  }

  &:focus-visible {
    outline: 2px solid var(--soremo-blue);
    outline-offset: 2px;
    border-radius: ${({ $variant }) => ($variant === "circle" ? "50%" : "4px")};
  }
`;

/**
 * disabled な button はポインターイベントを発火しないため、ToolTip の基準要素を
 * 常にこのラッパーへ統一する。
 */
const TooltipTarget = styled.span`
  display: inline-flex;
`;

/**
 * IconButton。forwardRef を維持しているのは Tippy ToolTip 経由でラップ
 * されたときに位置計測へ ref を渡す必要があるため。
 */
const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      icon,
      children,
      onClick,
      disabled = false,
      size = 24,
      variant = "default",
      filled,
      color = "var(--soremo-light-gray)",
      hoverColor = "var(--soremo-blue)",
      title,
      ariaLabel,
      href,
      target,
      className,
      style,
    },
    ref,
  ) => {
    const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
      if (disabled) return;
      onClick?.(e);
    };

    // icon prop が優先。未指定なら children（後方互換）を使う。
    const iconEl = (
      <span className="material-symbols-rounded">{icon ?? children}</span>
    );

    const styleProps = {
      $size: size,
      $variant: variant,
      $filled: filled,
      $color: color,
      $hoverColor: hoverColor,
      className,
      style,
    };

    // href 指定時は本物のリンク（<a>）として描画する。disabled のリンクは
    // HTML で表現できないため、disabled のときは従来の button にフォールバック。
    const button =
      href && !disabled ? (
        <StyledButton
          as="a"
          ref={ref as Ref<HTMLAnchorElement>}
          href={href}
          target={target}
          rel={target === "_blank" ? "noopener noreferrer" : undefined}
          onClick={
            onClick as unknown as MouseEventHandler<HTMLAnchorElement>
          }
          aria-label={ariaLabel ?? title}
          {...styleProps}
        >
          {iconEl}
        </StyledButton>
      ) : (
        <StyledButton
          ref={ref}
          type="button"
          onClick={handleClick}
          disabled={disabled}
          aria-label={ariaLabel ?? title}
          {...styleProps}
        >
          {iconEl}
        </StyledButton>
      );

    if (!title) return button;

    return (
      <ToolTip description={title}>
        <TooltipTarget>{button}</TooltipTarget>
      </ToolTip>
    );
  },
);

IconButton.displayName = "IconButton";

export default IconButton;
